import { DesktopTimePicker, PickerChangeHandlerContext, TimeValidationError } from '@mui/x-date-pickers'
import { getNestedValue } from '@tebuto/functions/formik'
import { cn } from '@tebuto/lib/utils'
import { useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import { type ComponentPropsWithRef, useEffect, useRef, useState } from 'react'
import { Label } from './Label'

const DEFAULT_TEXT_COLOR = 'hsl(var(--foreground-tertiary))'
const DEFAULT_BORDER_COLOR = 'hsl(var(--border-secondary))'
const DEFAULT_PRIMARY_COLOR = 'hsl(var(--primary-muted))'
const DEFAULT_PRIMARY_COLOR_LIGHT = 'hsl(var(--primary-light))'
const DEFAULT_PLACEHOLDER_COLOR = 'hsl(var(--foreground-muted))'

interface ITimeInput {
    label?: string
    name: string
    className?: string
    labelClassName?: string
    readOnly?: boolean
    value?: DateTime<true>
    minTime?: DateTime<true> | null
    maxTime?: DateTime<true> | null
    onChange?: (value: DateTime | null, context: PickerChangeHandlerContext<TimeValidationError>) => void
    // Theme-related props
    theme?: {
        backgroundColor?: string
        textColor?: string
        borderColor?: string
        primaryColor?: string
        primaryColorLight?: string
        textOnPrimary?: string
    }
    'data-testid'?: string
}

export function TimeInput({
    name,
    label,
    className = '',
    readOnly,
    value,
    minTime,
    maxTime,
    onChange,
    labelClassName,
    theme,
    'data-testid': dataTestId
}: ITimeInput) {
    const { values, setFieldValue, setFieldTouched } = useFormikContext()
    const [isOpen, setIsOpen] = useState(false)
    const initialHourRef = useRef<number | null>(null)
    const initialMinuteRef = useRef<number | null>(null)
    const hourSelectedRef = useRef<boolean>(false)
    const minuteSelectedRef = useRef<boolean>(false)
    const pickerRef = useRef<HTMLDivElement>(null)

    // Reset tracking when picker opens
    useEffect(() => {
        if (isOpen) {
            const currentValue = value ? value : getNestedValue(values, name)
            if (currentValue?.isValid) {
                initialHourRef.current = currentValue.hour
                initialMinuteRef.current = currentValue.minute
            } else {
                initialHourRef.current = null
                initialMinuteRef.current = null
            }
            hourSelectedRef.current = false
            minuteSelectedRef.current = false
        }
    }, [isOpen, value, values, name])

    // Handle clicks outside the picker
    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            const pickerDialog = document.getElementById('tebuto-time-input-dialog')

            // Check if click is outside both the picker dialog and the input container
            const isOutsideDialog = pickerDialog && !pickerDialog.contains(target)
            const isOutsideContainer = pickerRef.current && !pickerRef.current.contains(target)

            if (isOutsideDialog && isOutsideContainer) {
                setIsOpen(false)
            }
        }

        // Add a small delay to avoid closing immediately when opening
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 100)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    function onChangeHandler(newValue: DateTime | null, context: PickerChangeHandlerContext<TimeValidationError>) {
        setFieldValue(name, newValue)
        setFieldTouched(name)
        if (onChange) {
            onChange(newValue, context)
        }

        // Track when hour and minute are selected
        if (newValue?.isValid) {
            const currentHour = newValue.hour
            const currentMinute = newValue.minute

            // Check if hour was selected (changed from initial)
            if (initialHourRef.current !== currentHour) {
                hourSelectedRef.current = true
            }

            // Check if minute was selected (changed from initial)
            if (initialMinuteRef.current !== currentMinute) {
                minuteSelectedRef.current = true
            }

            // Close only when both hour and minute have been selected
            if (hourSelectedRef.current && minuteSelectedRef.current) {
                setIsOpen(false)
            }
        }
    }

    function handleClose() {
        setIsOpen(false)
    }

    // Default theme values - matches DateInput styling
    const defaultTheme = {
        backgroundColor: 'white',
        textColor: DEFAULT_TEXT_COLOR,
        borderColor: DEFAULT_BORDER_COLOR,
        primaryColor: DEFAULT_PRIMARY_COLOR,
        primaryColorLight: DEFAULT_PRIMARY_COLOR_LIGHT,
        textOnPrimary: '#ffffff'
    }

    // Use provided theme or defaults
    const currentTheme = theme ? { ...defaultTheme, ...theme } : defaultTheme
    const pickerTextStyles = {
        color: currentTheme.textColor,
        fontWeight: '400',
        fontSize: '0.875rem',
        lineHeight: '1.5rem',
        fontFamily: 'inherit'
    } as const

    const themedSx = {
        height: '40px',
        '& input': {
            padding: '8px',
            ...pickerTextStyles,
            '&::placeholder': { color: DEFAULT_PLACEHOLDER_COLOR, opacity: 1 }
        },
        '& .MuiPickersSectionList-root': pickerTextStyles,
        '& .MuiPickersInputBase-sectionsContainer': pickerTextStyles,
        '& .MuiPickersSectionList-section': pickerTextStyles,
        '& input:focus': { boxShadow: 'none', outline: 'none' },
        '& .MuiIconButton-root': { padding: '4px', marginRight: '2px' },
        '& .MuiPickersInputBase-root': {
            width: '100%',
            minWidth: 0,
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            backgroundColor: currentTheme.backgroundColor,
            border: `1px solid ${currentTheme.borderColor}`,
            overflow: 'hidden',
            '&:hover': { borderColor: '#9ca3af' },
            '&.Mui-focused': {
                borderColor: currentTheme.primaryColor,
                boxShadow: `0 0 0 1px ${currentTheme.primaryColor}`
            }
        },
        '& .MuiPickersInputBase-root svg': { height: '16px', width: '16px', color: DEFAULT_PLACEHOLDER_COLOR },
        '& .MuiInputAdornment-root': { margin: 0 },
        '& .MuiPickersOutlinedInput-root': {
            height: '40px',
            '& > fieldset': { border: 'none' },
            '&:hover > fieldset': { border: 'none' },
            '&.Mui-focused > fieldset': { border: 'none' }
        }
    }

    const themedPopperSx = {
        '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15), 0 4px 12px -2px rgba(0, 0, 0, 0.08)',
            border: '1px solid #f3f4f6',
            overflow: 'hidden',
            marginTop: '4px'
        },
        '& .MuiMultiSectionDigitalClock-root': {
            padding: '8px'
        },
        '& ul.MuiList-root': {
            msOverflowStyle: 'none' as const,
            scrollbarWidth: 'none' as const,
            '&::-webkit-scrollbar': { display: 'none' },
            '&::after': { height: '0' },
            padding: '4px',
            '& li': {
                fontSize: '13px',
                fontFamily: 'inherit',
                color: currentTheme.textColor,
                fontWeight: '500',
                borderRadius: '8px',
                margin: '2px 0',
                transition: 'all 0.15s ease'
            },
            '& li.Mui-selected': {
                color: currentTheme.textOnPrimary,
                backgroundColor: currentTheme.primaryColor,
                fontWeight: '600',
                '&:hover': {
                    backgroundColor: '#009087'
                }
            },
            '& li:hover:not(.Mui-selected)': {
                backgroundColor: currentTheme.primaryColorLight,
                color: currentTheme.primaryColor
            }
        }
    }

    return (
        <div ref={pickerRef} className={cn('relative max-w-full min-w-0', label && 'flex flex-col', className)}>
            {label && (
                <Label htmlFor={name} className={labelClassName}>
                    {label}
                </Label>
            )}
            <DesktopTimePicker
                value={value ? value : getNestedValue(values, name)}
                open={isOpen}
                onChange={onChangeHandler}
                onOpen={() => setIsOpen(true)}
                onClose={handleClose}
                onAccept={() => setIsOpen(false)}
                closeOnSelect={false}
                readOnly={readOnly}
                minTime={minTime?.isValid ? minTime : undefined}
                maxTime={maxTime?.isValid ? maxTime : undefined}
                sx={themedSx}
                slotProps={{
                    actionBar: { actions: [] },
                    textField: {
                        fullWidth: true,
                        ...(dataTestId
                            ? {
                                  slotProps: {
                                      htmlInput: { 'data-testid': dataTestId } as ComponentPropsWithRef<'input'>
                                  }
                              }
                            : {})
                    },
                    desktopPaper: {
                        id: 'tebuto-time-input-dialog',
                        onClick: (e: React.MouseEvent) => {
                            // Prevent clicks inside the paper from bubbling up
                            e.stopPropagation()
                        }
                    },
                    popper: {
                        sx: themedPopperSx,
                        container:
                            typeof window !== 'undefined' ? document.getElementById('tebuto-booking-widget') : undefined
                    }
                }}
            />
        </div>
    )
}
